// A run's state in the Analyses row grammar (DESIGN_SPEC §8, §12.17; RESTYLE §5), for Submit's recent runs and the
// seen-before line.
//   recordStatus(job) → { state, verdict, source }     the record's view; needsStream(job) says when the stream must be read
//   <RunMark status overdue? note? className? />        the filled verdict banner with its score · LIVE · QUEUED (+ note) ·
//                                                       failed / cancelled / completed-without-verdict in ink; a run on a
//                                                       node past its timeout keeps LIVE without the ping, plus "overdue"

//   useStreamStatus(job) · <StreamedStatus job>{(status) => …}</StreamedStatus>   read the job's SSE stream (shared
//       connection) for a live or verdict-less record, so "dispatched" reads "running" and a finished run shows its verdict
import { useMemo, type ReactNode } from "react";
import { JobState, LiveBadge, QueuedBadge, VerdictBanner, type JobStateName } from "@/components/ui";
import { useJobStream } from "@/hooks";
import type { EffectiveState, Job } from "@/types";

type RunStatus = {
  state: EffectiveState;
  verdict: { label: "benign" | "suspicious" | "malicious"; score: number } | null;
  /** The record says one thing and the stream another (or the stream has not caught up). */
  source: "stream" | "record";
};

export function recordStatus(job: Job): RunStatus {
  return { state: job.state, verdict: job.verdict ? { label: job.verdict.label, score: job.verdict.score } : null, source: "record" };
}

const LIVE = new Set<EffectiveState>(["dispatched", "provisioning", "booting", "running", "collecting"]);
export const isLiveState = (s: EffectiveState) => LIVE.has(s);
const isTerminalRecord = (j: Job) => j.state === "completed" || j.state === "failed" || j.state === "cancelled";
/** Records that need the stream to tell their state or verdict. */
export const needsStream = (j: Job) => !!j.node_id && (!isTerminalRecord(j) || (j.state === "completed" && !j.verdict));

function useStreamStatus(job: Job): RunStatus {
  const stream = useJobStream(job.id, job);
  const label = stream.verdict?.label ?? job.verdict?.label;
  const score = stream.verdict?.score ?? job.verdict?.score;
  return useMemo(
    () => ({ state: stream.state, verdict: label !== undefined && score !== undefined ? { label, score } : null, source: stream.stateSource }),
    [stream.state, stream.stateSource, label, score],
  );
}

export function StreamedStatus({ job, children }: { job: Job; children: (s: RunStatus) => ReactNode }) {
  return <>{children(useStreamStatus(job))}</>;
}

export function RunMark({ status, overdue, note, className }: { status: RunStatus; overdue?: boolean; note?: ReactNode; className?: string }) {
  const { state, verdict } = status;
  if (state === "completed" && verdict) return <VerdictBanner v={verdict.label} score={verdict.score} size="sm" className={className} />;
  if (isLiveState(state) && overdue)
    return (
      <span className="inline-flex items-center gap-2">
        <LiveBadge size="sm" ping={false} />
        <span className="meta leading-none">overdue</span>
      </span>
    );
  if (isLiveState(state)) return <LiveBadge size="sm" />;
  if (state === "queued") {
    return (
      <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
        <QueuedBadge size="sm" />
        {note && <span className="meta">{note}</span>}
      </span>
    );
  }
  return <JobState s={state as JobStateName} note={note} />;
}
